import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Divider,
  Flex,
  Modal,
  theme,
  Typography,
} from 'antd';
import {
  CSSProperties,
  Fragment,
  SVGProps,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PreSalesCancellationReason, PreSalesQualification, PreSalesQuestion } from '../../../dto/model';
import { strings } from '../../../lang';
import { PreSalesAnswers } from '../../../utils/pre-sales-questionnaire-evaluator';
import { isUnifiedPreSalesProductEnabled } from '../../../utils/unified-pre-sales-pv';
import { isPvInterested, isPvOnlyInterest } from '../pre-sales-pv-handover';
import type { PreSalesCancellationEvaluationStatus } from './pre-sales-questionnaire-types';
import { formatPreSalesCancellationReason } from './pre-sales-questionnaire-utils';

const { Text } = Typography;

type PreSalesProduct = 'WP' | 'PV';
type PreSalesProductStatus = 'NotInterested' | 'Eligible' | 'NotEligible' | 'Pending';

type PreSalesProductOverviewProps = {
  unifiedFlow?: boolean;
  qualification?: PreSalesQualification;
  answers: PreSalesAnswers;
  visibleQuestions: PreSalesQuestion[];
  cancellationReasons: PreSalesCancellationReason[];
  evaluationStatus: PreSalesCancellationEvaluationStatus;
};

type ProductOverviewItem = {
  product: PreSalesProduct;
  disabled: boolean;
  reasons: PreSalesCancellationReason[];
  status: PreSalesProductStatus;
};

export const getPreSalesProductOverviewItems = (
  answers: PreSalesAnswers,
  visibleQuestions: PreSalesQuestion[],
  cancellationReasons: PreSalesCancellationReason[],
  qualification?: PreSalesQualification,
  unifiedFlow = false,
): ProductOverviewItem[] => {
  if (unifiedFlow) {
    return (['WP', 'PV'] as const).filter(isUnifiedPreSalesProductEnabled).map((product) => {
      const qualifiedProduct = qualification?.products.find((item) =>
        item.product === (product === 'WP' ? 'HP' : product));
      const status: PreSalesProductStatus = qualifiedProduct?.status === 'Buildable'
        ? 'Eligible'
        : qualifiedProduct?.status === 'NotBuildable'
          ? 'NotEligible'
          : qualifiedProduct?.status === 'NotInterested' ? 'NotInterested' : 'Pending';
      return {
        product,
        status,
        disabled: status !== 'Eligible',
        reasons: qualifiedProduct?.cancellationReasons.map((reason) => ({ ...reason, product })) ?? [],
      };
    });
  }
  const reasonsByProduct = (product: PreSalesProduct) =>
    cancellationReasons.filter((reason) => reason.product === product);
  const wpReasons = reasonsByProduct('WP');
  const pvReasons = reasonsByProduct('PV');
  const getProductStatus = (
    interested: boolean,
    reasons: PreSalesCancellationReason[],
  ): PreSalesProductStatus => {
    if (reasons.length > 0) return 'NotEligible';
    if (!interested) return 'NotInterested';
    return 'Eligible';
  };
  const wpStatus = getProductStatus(
    !isPvOnlyInterest(answers.pv_interest),
    wpReasons,
  );
  const pvStatus = getProductStatus(
    visibleQuestions.some((question) => question.questionKey === 'pv_interest')
      && isPvInterested(answers.pv_interest),
    pvReasons,
  );

  return [
    {
      product: 'WP',
      disabled: wpStatus === 'NotEligible',
      reasons: wpReasons,
      status: wpStatus,
    },
    {
      product: 'PV',
      disabled: pvStatus === 'NotInterested' || pvStatus === 'NotEligible',
      reasons: pvReasons,
      status: pvStatus,
    },
  ];
};

const HeatPumpIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 16 16" fill="none" {...props}>
    <path
      d="M12.4451 3.3335H3.55621C2.32891 3.3335 1.33398 4.22893 1.33398 5.3335V10.6668C1.33398 11.7714 2.32891 12.6668 3.55621 12.6668H12.4451C13.6724 12.6668 14.6673 11.7714 14.6673 10.6668V5.3335C14.6673 4.22893 13.6724 3.3335 12.4451 3.3335Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10.668 6H12.668M10.668 8H12.668M10.668 10H12.668" stroke="currentColor" strokeLinecap="round" />
    <path d="M5.99951 8.00041C6.81729 8.00041 7.48099 7.40485 7.48099 6.66707C7.48099 5.9293 6.81729 5.33374 5.99951 5.33374V8.00041Z" fill="currentColor" />
    <path d="M5.99951 8.00024C5.99951 8.81802 6.59507 9.48173 7.33285 9.48173C8.07062 9.48173 8.66618 8.81802 8.66618 8.00024H5.99951Z" fill="currentColor" />
    <path d="M6.00052 8.00049C5.18275 8.00049 4.51904 8.59604 4.51904 9.33382C4.51904 10.0716 5.18275 10.6672 6.00052 10.6672V8.00049Z" fill="currentColor" />
    <path d="M6.00041 8.00052C6.00041 7.18275 5.40485 6.51904 4.66707 6.51904C3.9293 6.51904 3.33374 7.18275 3.33374 8.00052H6.00041Z" fill="currentColor" />
  </svg>
);

const PvIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M8 2a4 4 0 1 0 8 0M4 3h1M19 3h1M12 9v1M17.2 7.2l.707.707M6.8 7.2l-.7.7M4.28 21h15.44a1 1 0 0 0 .97-1.243l-1.5-6a1 1 0 0 0-.97-.757H5.78a1 1 0 0 0-.97.757l-1.5 6A1 1 0 0 0 4.28 21ZM4 17h16M10 13l-1 8M14 13l1 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const productLabel = (product: PreSalesProduct): string =>
  product === 'WP'
    ? strings.preSalesQuestionnaire.heatPumpShortLabel
    : strings.preSalesQuestionnaire.pvShortLabel;

const productStatusLabel = (status: PreSalesProductStatus): string => {
  switch (status) {
    case 'Pending':
      return strings.preSalesQuestionnaire.productStatusPending;
    case 'NotInterested':
      return strings.preSalesQuestionnaire.productStatusNotInterested;
    case 'Eligible':
      return strings.preSalesQuestionnaire.productStatusEligible;
    case 'NotEligible':
      return strings.preSalesQuestionnaire.productStatusNotEligible;
  }
};

export const PreSalesProductOverview = ({
  answers,
  visibleQuestions,
  cancellationReasons,
  evaluationStatus,
  unifiedFlow,
  qualification,
}: PreSalesProductOverviewProps) => {
  const { token } = theme.useToken();
  const [selectedProduct, setSelectedProduct] = useState<PreSalesProduct | null>(null);
  const items = useMemo(
    () => getPreSalesProductOverviewItems(
      answers,
      visibleQuestions,
      cancellationReasons,
      qualification,
      unifiedFlow,
    ),
    [answers, cancellationReasons, visibleQuestions, qualification, unifiedFlow],
  );
  const selectedItem = items.find((item) => item.product === selectedProduct);
  const selectedReasonsCount = selectedItem?.reasons.length ?? 0;

  useEffect(() => {
    if (selectedProduct && selectedReasonsCount === 0) {
      setSelectedProduct(null);
    }
  }, [selectedProduct, selectedReasonsCount]);

  const getRowStyle = (item: ProductOverviewItem): CSSProperties => item.disabled
    ? {
      backgroundColor: item.status === 'NotInterested'
        ? token.colorBgContainerDisabled
        : token.colorBgContainer,
      color: token.colorTextDisabled,
    }
    : {
      backgroundColor: token.colorBgContainer,
      color: token.colorText,
    };
  const getIconColor = (item: ProductOverviewItem): string => {
    if (item.disabled) return token.colorTextDisabled;
    return item.product === 'WP' ? token.purple6 : token.gold6;
  };

  return (
    <Card
      aria-labelledby="bp-presales-product-overview-title"
      size="small"
      styles={{ body: { padding: 0 } }}
    >
      <Flex vertical>
        <Text
          id="bp-presales-product-overview-title"
          strong
          style={{
            display: 'block',
            padding: `${token.paddingSM}px ${token.paddingSM}px ${token.paddingXXS}px`,
          }}
          type="secondary"
        >
          {strings.preSalesQuestionnaire.productOverviewTitle.toUpperCase()}
        </Text>

        {evaluationStatus === 'error' && (
          <Text
            style={{
              display: 'block',
              padding: `${token.paddingXXS}px ${token.paddingSM}px ${token.paddingXS}px`,
            }}
            type="danger"
          >
            {strings.preSalesQuestionnaire.cancellationCheckUnavailable}
          </Text>
        )}

        <Flex vertical>
          {items.map((item) => {
            const label = productLabel(item.product);
            return (
              <Fragment key={item.product}>
                {item.product === 'PV' && <Divider style={{ margin: 0 }} />}
                <Flex
                  align="center"
                  gap={6}
                  className="bp-presales-product-overview__row"
                  style={{
                    ...getRowStyle(item),
                    minHeight: 52,
                    padding: `${token.paddingXS}px ${token.paddingSM}px`,
                  }}
                >
                  <Flex
                    aria-hidden
                    align="center"
                    justify="center"
                    style={{ color: getIconColor(item), flex: '0 0 18px', height: 18 }}
                  >
                    {item.product === 'WP'
                      ? <HeatPumpIcon width={18} height={18} />
                      : <PvIcon width={18} height={18} />}
                  </Flex>
                  <Flex vertical gap={2} flex="1 1 0" style={{ minWidth: 0 }}>
                    <Text strong disabled={item.disabled}>{label}</Text>
                    <Text
                      disabled={item.disabled}
                      type={item.status === 'Eligible' ? 'success' : undefined}
                    >
                      {productStatusLabel(item.status)}
                    </Text>
                  </Flex>
                  {item.status === 'NotEligible' && item.reasons.length > 0 && (
                    <Button
                      aria-label={strings.preSalesQuestionnaire.showReasons}
                      color='danger'
                      variant="filled"
                      icon={<ExclamationCircleOutlined />}
                      onClick={() => setSelectedProduct(item.product)}
                    >
                      {strings.preSalesQuestionnaire.showReasons}
                    </Button>
                  )}
                </Flex>
              </Fragment>
            );
          })}
        </Flex>
      </Flex>

      <Modal
        footer={null}
        open={Boolean(selectedItem?.reasons.length)}
        title={selectedProduct
          ? strings.preSalesQuestionnaire.productCancellationReasonsTitle(
            productLabel(selectedProduct),
          )
          : undefined}
        onCancel={() => setSelectedProduct(null)}
      >
        <ul className="bp-presales-questionnaire__cancellation-list">
          {selectedItem?.reasons.map((reason, index) => (
            <li key={`${reason.message}-${index}`}><Text>{formatPreSalesCancellationReason(reason)}</Text></li>
          ))}
        </ul>
      </Modal>
    </Card>
  );
};
